
const libFableServiceProviderBase = require('fable-serviceproviderbase');

/**
 * Render a server-side Document into the form viewer / editor and forward the
 * user to the rendered URL.  This codifies the pattern Fieldbook, the legacy
 * DocumentProvider, the sampleplan helper, and the OfflinePreview view all use:
 *
 *   1.  GET  Report/Render/{IDDocument}/{Variation}     →  { GUIDReportDescription }
 *   2.  GET  CheckoutSessionToken                       →  { Token }   (optional)
 *   3.  Navigate to {apiBase}Report/{GUID}/index.html[?SessionToken=...]
 *
 * The render endpoint is synchronous from the caller's perspective — the API
 * server stages the rendered page and returns the GUID once it is ready, so
 * "wait for it to render" just means "wait for the HTTP response."  No polling.
 *
 * The CheckoutSessionToken step is only needed when the rendered URL lives on
 * a different origin than the calling page (e.g. smartforms iframing into the
 * API host).  When the API is reached on the same origin via Kong the token is
 * unnecessary.
 *
 * Reference implementations:
 *   web/sampleplan/src/helpers/openDocument.js
 *   web/providers/DocumentProvider.js  (openFormByIDDocument)
 *   web/headlight-form-editor/source/views/OfflinePreview.js  (_renderExistingDocumentShell)
 */
class WebHeadlightDocumentRenderService extends libFableServiceProviderBase
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		this.serviceType = 'HeadlightDocumentRender';

		// Optional override: pass `RestClient: <instance>` in options to bypass
		// the discovery logic in `_restClient`.  Useful for tests, the example
		// app, and any host that wires up its own client.
		//
		// Read from `this.options` (already normalised by the base constructor)
		// rather than the raw `pOptions` parameter, so both calling styles work:
		//   new Service(fable, { RestClient: x })   → super stores it in this.options
		//   new Service({ RestClient: x })          → super treats arg 1 as options
		this._OverrideRestClient = (this.options && this.options.RestClient) ? this.options.RestClient : null;
	}

	/**
	 * Resolve the HeadlightRestClient instance regardless of where it lives.
	 * Discovery order:
	 *   1. options.RestClient (constructor override)
	 *   2. fable.HeadlightRestClient
	 *   3. fable.pict.HeadlightRestClient
	 */
	get _restClient()
	{
		if (this._OverrideRestClient) { return this._OverrideRestClient; }
		if (this.fable && this.fable.HeadlightRestClient) { return this.fable.HeadlightRestClient; }
		if (this.fable && this.fable.pict && this.fable.pict.HeadlightRestClient) { return this.fable.pict.HeadlightRestClient; }
		return null;
	}

	setRestClient(pClient)
	{
		this._OverrideRestClient = pClient || null;
	}

	/**
	 * Kick off a server-side render of a Document.
	 *
	 * @param {number} pIDDocument        The IDDocument to render.
	 * @param {string|((pError: Error|null, pResult?: { GUIDReportDescription: string, IndexURL: string }) => void)} pVariation
	 *                                    The render variation (default 'default').  Other known values: 'html'.
	 *                                    Pass the callback here for the shorthand `renderDocument(id, callback)`.
	 * @param {(pError: Error|null, pResult?: { GUIDReportDescription: string, IndexURL: string }) => void} [fCallback]
	 */
	renderDocument(pIDDocument, pVariation, fCallback)
	{
		// Allow `renderDocument(id, callback)` shorthand.
		if (typeof pVariation === 'function')
		{
			fCallback = pVariation;
			pVariation = null;
		}
		const tmpVariation = pVariation || 'default';
		const tmpCallback = (typeof fCallback === 'function') ? fCallback : (() => {});
		const tmpClient = this._restClient;

		if (!tmpClient)
		{
			return tmpCallback(new Error('HeadlightDocumentRender: HeadlightRestClient is not available.'));
		}
		if (!pIDDocument)
		{
			return tmpCallback(new Error('HeadlightDocumentRender: IDDocument is required.'));
		}

		tmpClient.getJSON(`Report/Render/${pIDDocument}/${tmpVariation}`, (pError, pBody) =>
		{
			if (pError) { return tmpCallback(pError); }
			if (pBody && pBody.Error)
			{
				return tmpCallback(new Error(typeof pBody.Error === 'string' ? pBody.Error : 'Render returned an error.'));
			}
			if (!pBody || !pBody.GUIDReportDescription)
			{
				return tmpCallback(new Error('HeadlightDocumentRender: render response missing GUIDReportDescription.'));
			}
			return tmpCallback(null,
				{
					GUIDReportDescription: pBody.GUIDReportDescription,
					IndexURL: tmpClient.formatUrl(`Report/${pBody.GUIDReportDescription}/index.html`)
				});
		});
	}

	/**
	 * Fetch a short-lived session token suitable for cross-origin embedding of
	 * the rendered report.
	 *
	 * @param {(pError: Error|null, pToken?: { Token: string }) => void} fCallback
	 */
	getCheckoutSessionToken(fCallback)
	{
		const tmpCallback = (typeof fCallback === 'function') ? fCallback : (() => {});
		const tmpClient = this._restClient;
		if (!tmpClient)
		{
			return tmpCallback(new Error('HeadlightDocumentRender: HeadlightRestClient is not available.'));
		}
		tmpClient.getJSON('CheckoutSessionToken', (pError, pBody) =>
		{
			if (pError) { return tmpCallback(pError); }
			return tmpCallback(null, pBody || {});
		});
	}

	/**
	 * Render a Document and navigate a window to the rendered page, mirroring
	 * the pattern in Fieldbook's openFormByIDDocument and sampleplan's
	 * openDocument helper.
	 *
	 * Options:
	 *   target              - '_self' | '_blank' | window reference (default '_blank')
	 *   variation           - render variation passed to Report/Render (default 'default')
	 *   includeSessionToken - if true, fetches CheckoutSessionToken and appends
	 *                         ?SessionToken=… to the URL.  Required when the
	 *                         rendered URL lives on a different origin than the
	 *                         calling page.  Default false (same-origin via Kong).
	 *   loadingHTML         - HTML written to a freshly-opened window while the
	 *                         render request is in flight.  Pass `false` to skip
	 *                         the loading splash.
	 *
	 * @param {number}   pIDDocument
	 * @param {object}   [pOptions]
	 * @param {(pError: Error|null, pURL?: string) => void} [fCallback]
	 */
	openDocument(pIDDocument, pOptions, fCallback)
	{
		const tmpOptions = pOptions || {};
		const tmpCallback = (typeof fCallback === 'function') ? fCallback : (() => {});
		const tmpTarget = tmpOptions.target || '_blank';
		const tmpVariation = tmpOptions.variation || 'default';
		const tmpIncludeToken = tmpOptions.includeSessionToken === true;

		// Resolve the destination window.
		let tmpWindowRef = null;
		const tmpHasWindow = (typeof window !== 'undefined');
		const tmpOpenedWindow = (tmpTarget !== '_self' && typeof tmpTarget !== 'object');

		if (tmpHasWindow)
		{
			if (tmpTarget === '_self')
			{
				tmpWindowRef = window;
			}
			else if (typeof tmpTarget === 'object' && tmpTarget !== null)
			{
				tmpWindowRef = tmpTarget;
			}
			else
			{
				tmpWindowRef = window.open('', tmpTarget);
				if (!tmpWindowRef)
				{
					return tmpCallback(new Error('HeadlightDocumentRender: window.open was blocked by the browser.'));
				}
				if (tmpOptions.loadingHTML !== false)
				{
					try
					{
						tmpWindowRef.document.write(tmpOptions.loadingHTML || this._defaultLoadingHTML());
					}
					catch (e)
					{
						if (this.fable && this.fable.log)
						{
							this.fable.log.warn(`HeadlightDocumentRender: could not write loading splash: ${e.message || e}`);
						}
					}
				}
			}
		}

		const tmpFinish = (pError, pURL) =>
		{
			if (pError && tmpOpenedWindow && tmpWindowRef)
			{
				try { tmpWindowRef.close(); } catch (e) { /* ignore */ }
			}
			return tmpCallback(pError, pURL);
		};

		this.renderDocument(pIDDocument, tmpVariation, (pRenderError, pRenderResult) =>
		{
			if (pRenderError) { return tmpFinish(pRenderError); }

			const tmpNavigate = (pToken) =>
			{
				let tmpFinalURL = pRenderResult.IndexURL;
				if (pToken && pToken.Token)
				{
					tmpFinalURL += (tmpFinalURL.indexOf('?') === -1 ? '?' : '&');
					tmpFinalURL += 'SessionToken=' + encodeURIComponent(pToken.Token);
				}
				// Collapse accidental // (other than after the protocol).
				tmpFinalURL = tmpFinalURL.replace(/([^:])\/\//g, '$1/');

				if (tmpWindowRef)
				{
					try { tmpWindowRef.location = tmpFinalURL; }
					catch (e) { return tmpFinish(e); }
				}
				return tmpFinish(null, tmpFinalURL);
			};

			if (tmpIncludeToken)
			{
				this.getCheckoutSessionToken((pTokenError, pToken) =>
				{
					if (pTokenError) { return tmpFinish(pTokenError); }
					return tmpNavigate(pToken);
				});
			}
			else
			{
				return tmpNavigate(null);
			}
		});
	}

	/**
	 * Get the URL to download a Document's rendered PDF.  Same-origin via the
	 * REST client's serverURL, so a plain anchor `<a download>` works.
	 */
	getPDFURL(pIDDocument)
	{
		const tmpClient = this._restClient;
		if (!tmpClient || !pIDDocument) { return null; }
		return tmpClient.formatUrl(`Document/${pIDDocument}/DCR.pdf`);
	}

	/**
	 * Fire-and-forget PDF download.  Synthesizes a hidden `<a download>` to
	 * the same-origin PDF URL and clicks it — the browser handles the rest.
	 *
	 * Use this when you don't need to know when the file arrives (no spinner,
	 * no toast, no follow-up action).  No callback fires after the download
	 * completes; the click is the only signal you'll see.
	 *
	 * Use {@link downloadPDF} instead if you need to show progress UI, gate
	 * follow-up work on completion, or surface server errors to the user.
	 *
	 * Options:
	 *   filename - download filename hint passed to the anchor.  Default
	 *              `Document-{IDDocument}.pdf`.  Honoured by the browser only
	 *              for same-origin URLs that don't set their own
	 *              Content-Disposition.
	 *
	 * @param {number}  pIDDocument
	 * @param {object}  [pOptions]
	 * @returns {string|null} The PDF URL that was triggered, or null when no
	 *                        rest client is available.
	 */
	downloadPDFLink(pIDDocument, pOptions)
	{
		const tmpOptions = pOptions || {};
		const tmpURL = this.getPDFURL(pIDDocument);
		if (!tmpURL) { return null; }
		if (typeof document === 'undefined' || typeof document.createElement !== 'function')
		{
			if (this.fable && this.fable.log) { this.fable.log.warn('HeadlightDocumentRender: downloadPDFLink requires a browser environment.'); }
			return null;
		}

		const tmpFilename = tmpOptions.filename || `Document-${pIDDocument}.pdf`;
		const tmpAnchor = document.createElement('a');
		tmpAnchor.href = tmpURL;
		tmpAnchor.download = tmpFilename;
		tmpAnchor.rel = 'noopener';
		tmpAnchor.style.display = 'none';
		try
		{
			document.body.appendChild(tmpAnchor);
			tmpAnchor.click();
		}
		finally
		{
			if (tmpAnchor.parentNode === document.body) { document.body.removeChild(tmpAnchor); }
		}
		return tmpURL;
	}

	/**
	 * Lifecycle-aware PDF download.  Fetches the PDF as a Blob (so the
	 * callback fires only after the file is fully available — important for
	 * documents that take a long time to render), then synthesizes a download
	 * via a hidden `<a download>` + object URL.
	 *
	 * If the configured rest client exposes a `getBlob(path, callback)`
	 * method it is preferred (so any client-defined headers — `X-Upstream`,
	 * session tokens, etc. — are applied).  Otherwise the service falls back
	 * to a credentialed global `fetch` against the same-origin URL.
	 *
	 * Options:
	 *   filename - download filename hint passed to the anchor.  Default
	 *              `Document-{IDDocument}.pdf`.
	 *
	 * Callback signature: `(error, { url, filename, blob })` on success, or
	 * `(error)` on failure.
	 *
	 * @param {number}  pIDDocument
	 * @param {object}  [pOptions]
	 * @param {(pError: Error|null, pResult?: { url: string, filename: string, blob: Blob }) => void} [fCallback]
	 */
	downloadPDF(pIDDocument, pOptions, fCallback)
	{
		// Allow `downloadPDF(id, callback)` shorthand.
		if (typeof pOptions === 'function')
		{
			fCallback = pOptions;
			pOptions = null;
		}
		const tmpOptions = pOptions || {};
		const tmpCallback = (typeof fCallback === 'function') ? fCallback : (() => {});

		const tmpURL = this.getPDFURL(pIDDocument);
		if (!tmpURL)
		{
			return tmpCallback(new Error('HeadlightDocumentRender: HeadlightRestClient is not available.'));
		}
		if (typeof document === 'undefined' || typeof document.createElement !== 'function')
		{
			return tmpCallback(new Error('HeadlightDocumentRender: downloadPDF requires a browser environment.'));
		}

		const tmpFilename = tmpOptions.filename || `Document-${pIDDocument}.pdf`;

		const tmpDeliver = (pBlob) =>
		{
			let tmpObjectURL;
			let tmpAnchor;
			try
			{
				tmpObjectURL = URL.createObjectURL(pBlob);
				tmpAnchor = document.createElement('a');
				tmpAnchor.href = tmpObjectURL;
				tmpAnchor.download = tmpFilename;
				tmpAnchor.rel = 'noopener';
				tmpAnchor.style.display = 'none';
				document.body.appendChild(tmpAnchor);
				tmpAnchor.click();
			}
			catch (pError)
			{
				return tmpCallback(pError);
			}
			finally
			{
				if (tmpAnchor && tmpAnchor.parentNode === document.body)
				{
					document.body.removeChild(tmpAnchor);
				}
				// Revoke after a tick so the click has time to start the download.
				if (tmpObjectURL)
				{
					setTimeout(() => { try { URL.revokeObjectURL(tmpObjectURL); } catch (e) { /* ignore */ } }, 5000);
				}
			}
			return tmpCallback(null, { url: tmpURL, filename: tmpFilename, blob: pBlob });
		};

		const tmpClient = this._restClient;
		if (tmpClient && typeof tmpClient.getBlob === 'function')
		{
			tmpClient.getBlob(`Document/${pIDDocument}/DCR.pdf`, (pError, pBlob) =>
			{
				if (pError) { return tmpCallback(pError); }
				if (!pBlob) { return tmpCallback(new Error('HeadlightDocumentRender: PDF download returned no body.')); }
				return tmpDeliver(pBlob);
			});
			return;
		}

		if (typeof fetch !== 'function')
		{
			return tmpCallback(new Error('HeadlightDocumentRender: PDF download requires either a rest client with getBlob() or a global fetch implementation.'));
		}
		fetch(tmpURL, { credentials: 'include' })
			.then((pResponse) =>
			{
				if (!pResponse.ok) { throw new Error(`HTTP ${pResponse.status} from ${tmpURL}`); }
				return pResponse.blob();
			})
			.then((pBlob) => tmpDeliver(pBlob))
			.catch((pError) => tmpCallback(pError));
	}

	_defaultLoadingHTML()
	{
		return /*html*/`<!DOCTYPE html><html><head><title>Loading…</title>
<style>
@-webkit-keyframes load8 { 0% { -webkit-transform: rotate(0deg); transform: rotate(0deg); } 100% { -webkit-transform: rotate(360deg); transform: rotate(360deg); } }
@keyframes load8 { 0% { -webkit-transform: rotate(0deg); transform: rotate(0deg); } 100% { -webkit-transform: rotate(360deg); transform: rotate(360deg); } }
*, *:before, *:after { box-sizing: border-box; }
body { padding: 15px 0; margin: 0 auto; background-color: #53565A; font-family: Arial, sans-serif; width: 100%; font-size: 15px; max-width: 1000px; }
.loading-splash-screen { width: 100%; height: 100vh; background: #53565A; position: fixed; top: 0; left: 0; text-align: center; z-index: 10002; display: flex; align-items: center; }
.loading-splash-screen .loading-spinner, .loading-splash-screen .loading-spinner:after { border-radius: 50%; width: 15em; height: 15em; }
.loading-spinner { margin: 60px auto; font-size: 10px; position: relative; text-indent: -9999em; border-top: 1.1em solid rgba(255, 255, 255, 0.2); border-right: 1.1em solid rgba(255, 255, 255, 0.2); border-bottom: 1.1em solid rgba(255, 255, 255, 0.2); border-left: 1.1em solid #ffffff; -webkit-transform: translateZ(0); -ms-transform: translateZ(0); transform: translateZ(0); -webkit-animation: load8 1.1s infinite linear; animation: load8 1.1s infinite linear; color: #ffffff; }
</style></head><body>
<div class="loading-splash-screen"><div class="loading-spinner">Loading…</div></div>
</body></html>`;
	}
}

module.exports = WebHeadlightDocumentRenderService;

module.exports.default_configuration = (
{
	Name: 'HeadlightDocumentRender',
	ServiceType: 'HeadlightDocumentRender'
});
